import { configs } from '../constants/configs/configs';
import { localStorageHyperDashboardTools } from '../utils/tools/localStorageHyperDashboardTools';
import { SPINNER_MESSAGES_IDS } from '../constants/common/miscellaneous';

/**
 * @async
 * @param {Object} response - data we retrieve from server
 * @param {Function} dispatch
 * @param {Function} setError
 * @param actions
 * @param shouldShowSpinner
 * @returns {Promise<*>}:
 *  {
 *      errorMessage {null, string },   - message we would like to show on UI for particular error
 *      errorType {null, string},       - error type
 *  }
 */

const { routes } = configs;
const checkResponse = async (response, dispatch, setError, actions, shouldShowSpinner, emptyResonse) => {
    const { status, url } = response;

    const { stopSpinner } = actions;

    const time = new Date();

    const errorAdditionalInfo = {
        time: `${time.toLocaleString()}, time zone offset: ${time.getTimezoneOffset()}`,
        endpoint: url,
        code: status,
    };

    shouldShowSpinner && dispatch(stopSpinner());

    switch (true) {
        case status === 200 || status === 201:
            if (emptyResonse) {
                return response;
            }
            return await response.json();
        case status === 401 || status === 408:
            return (window.location = routes.FALLBACK);
        default:
            return global.__ENV__.PRODUCTION && console.log(errorAdditionalInfo);
    }
};

/**
 * For start-custom, this will pass X-UID (user id) header with user id to mimic
 * logged-in user, so that individualPortal can render based on the real data.
 *
 * v.lenin@yopmail.com - nv2dev - 2785
 * h@yopmail.com - nv2dev
 * steve.johnson190130024610@yopmail.com - camainqa - 4940758
 */

const userCredentials = (credentials => (credentials !== 'default' ? JSON.parse(credentials) : {}))(
    localStorageHyperDashboardTools.getValueByPropName('userCredentials', 'default'),
);

const additionalHeaderPropertiesForCustomMode = configs.ENV.CUSTOM ? userCredentials : {};

/**
 * Sets object values if value exists.
 * @param name
 * @param value
 * @returns {{}}
 */
const extendHttpRequestHeaders = (name, value) => {
    let headers = {};
    if (value) {
        headers[name] = value;
    }
    return headers;
};

/**
 * Check if app is in impersonate mode. If multitasking then redirect to Admin portal.
 */
const isInImpersonationModeMultiTasking = reduxCache => {
    const isImpersonatiDeatsilsCacheExist = reduxCache.common.environment.impersonationDetails;
    const isInImpersonationModeFromStorage = sessionStorage.getItem('isInImpersonationMode');
    const impersonationModeFromCache = reduxCache.common.environment.impersonation;
    const { landingPage, role } = reduxCache.common.environment.user;
    const isNotInImpersonationModeWithStorage =
        !impersonationModeFromCache && isInImpersonationModeFromStorage === 'true';
    const isAlreadyInImpersonationMode =
        isInImpersonationModeFromStorage === 'false' || isInImpersonationModeFromStorage === null;
    const impersonationOneId = localStorage.HHID;
    const impersonationTwoId = localStorage.HHID2;
    const isSameUser = impersonationTwoId ? impersonationOneId === impersonationTwoId : true;
    const currentImpersonationHouseholdId =
        isImpersonatiDeatsilsCacheExist && isImpersonatiDeatsilsCacheExist.householdId
            ? isImpersonatiDeatsilsCacheExist.householdId.toString()
            : null;
    const currentTabImpersonationHouseholdId = sessionStorage.getItem('impersonateHHID');
    const isSessionAndEnvironmentSame =
        currentTabImpersonationHouseholdId && currentImpersonationHouseholdId
            ? currentImpersonationHouseholdId === currentTabImpersonationHouseholdId
            : true;
    if (
        !isSessionAndEnvironmentSame ||
        isNotInImpersonationModeWithStorage ||
        (impersonationModeFromCache && isAlreadyInImpersonationMode) ||
        (impersonationModeFromCache && !isSameUser)
    ) {
        sessionStorage.setItem('isInImpersonationMode', false);
        localStorage.removeItem('HHID');
        localStorage.removeItem('HHID2');
        window.location.href = `${
            window.location.origin
        }/hix/account/user/switchNonDefRole/${role}?requestLandingPage=${encodeURIComponent(landingPage)}`;
    }
};

export default {
    /**
     * @async
     * @param {string} url - URL to download from
     * @param {Function} dispatch
     * @param {Object} actions
     * @param shouldShowSpinner
     * @returns {Promise<*>} - and we pass this Promise to <checkResponse> for:
     * a) handling errors;
     * b) extracting data on success request
     */
    getData: (url, dispatch, actions, shouldShowSpinner = true, reduxCache = null, returnResponseAsIs = false) => {
        const { setError, startSpinner } = actions;

        let fetchConfig = {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                cookie: '{cookie}',
                'Content-Type': 'application/json',
                ...additionalHeaderPropertiesForCustomMode,
            },
        };

        fetchConfig.headers = {
            ...fetchConfig.headers,
            ...extendHttpRequestHeaders('X-CSRF-TOKEN', window.csrf_token),
            ...extendHttpRequestHeaders('X-UID', window.UID),
            ...extendHttpRequestHeaders('X-EMAIL', window.EMAIL),
        };

        if (reduxCache && !(Object.keys(reduxCache).length === 0)) {
            isInImpersonationModeMultiTasking(reduxCache);
        }

        shouldShowSpinner && dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
        return fetch(url, fetchConfig)
            .then(response =>
                checkResponse(response, dispatch, setError, actions, shouldShowSpinner, returnResponseAsIs),
            )
            .catch(
                error => global.__ENV__.PRODUCTION && console.log('failed at endpoint ---', url, 'error ---', error),
            );
    },

    downloadFile: (url, dispatch, actions, shouldShowSpinner = true, additionalHeaders = {}) => {
        try {
            const { setError, startSpinner, stopSpinner } = actions;

            let fetchConfig = {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    cookie: '{cookie}',
                    'Content-Type': 'application/json',
                    ...additionalHeaders,
                },
            };

            fetchConfig.headers = {
                ...fetchConfig.headers,
                ...extendHttpRequestHeaders('X-CSRF-TOKEN', window.csrf_token),
                ...extendHttpRequestHeaders('X-UID', window.UID),
                ...extendHttpRequestHeaders('X-EMAIL', window.EMAIL),
            };

            shouldShowSpinner && dispatch(startSpinner(SPINNER_MESSAGES_IDS.DOWNLOADING_FILE));

            return fetch(url, fetchConfig)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to download file. Status: ${response.status}`);
                    }

                    const contentDisposition = response.headers.get('Content-Disposition');
                    let fileName = 'downloaded-file';
                    if (contentDisposition && contentDisposition.includes('filename=')) {
                        fileName = contentDisposition.split('filename=')[1].split(';')[0].replace(/["']/g, '');
                    }

                    return response.blob().then(blob => ({ blob, fileName }));
                })
                .then(({ blob, fileName }) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    link.click();
                    window.URL.revokeObjectURL(url);
                })
                .catch(error => {
                    dispatch(setError(error.message));
                    global.__ENV__.PRODUCTION &&
                        console.log('Download failed at endpoint ---', url, 'error ---', error);
                })
                .finally(() => {
                    shouldShowSpinner && dispatch(stopSpinner());
                });
        } catch (error) {
            console.log('downloadFile error', error);
        }
    },

    customGetData: (url, dispatch, actions,customHeaders, shouldShowSpinner = true ) => {
        const { setError, startSpinner } = actions;
        let fetchConfig = {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                cookie: '{cookie}',
                'Content-Type': 'application/json',
                ...customHeaders,
                ...additionalHeaderPropertiesForCustomMode,
            },
        };
        fetchConfig.headers = {
            ...fetchConfig.headers,
            ...extendHttpRequestHeaders('X-CSRF-TOKEN', window.csrf_token),
            ...extendHttpRequestHeaders('X-UID', window.UID),
            ...extendHttpRequestHeaders('X-EMAIL', window.EMAIL),
        };
        shouldShowSpinner && dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
        return fetch(url, fetchConfig)
            .then(response => checkResponse(response, dispatch, setError, actions, shouldShowSpinner))
            .catch(
                error => global.__ENV__.PRODUCTION && console.log('failed at endpoint ---', url, 'error ---', error),
            );
    },

    /**
     *
     * @param {Array} args - a) URL to download from; b) body
     * @returns {Promise<*>} - and we pass this Promise to <checkResponse> for:
     * a) handling errors;
     * b) extracting data on success request
     */
    postData: (...args) => {
        const [
            [
                url,
                body,
                method = 'POST',
                shouldShowSpinner = true,
                additionalHeaderProperties = undefined,
                contentType = undefined,
                responseExpected = true,
                emptyResonse = false,
                processAsFormData = false,
                loadingMessage = SPINNER_MESSAGES_IDS.SAVING_DATA,
            ],
            dispatch,
            actions,
        ] = args;

        const { setError, startSpinner } = actions;

        shouldShowSpinner && dispatch(startSpinner(loadingMessage));

        let requestBody;

        switch (true) {
            case contentType === 'application/x-www-form-urlencoded' || contentType === 'multipart/form-data':
                requestBody = { body };
                break;
            case method === 'GET': // Added for a while to support mock calls: providing 'GET' inside api.postData transforms 'POST' call to 'GET' call
                requestBody = {};
                break;
            default:
                requestBody = { body };
        }

        const contentTypeHeader =
            requestBody.body instanceof FormData ? {} : { 'Content-Type': contentType || 'application/json' };

        const fetchConfig = {
            method,
            mode: 'cors',
            credentials: 'include',
            headers: {
                // Include additional headers if provided
                ...(additionalHeaderProperties || {}),
                ...contentTypeHeader, // Ensure this is properly handled based on requestBody type
                'X-Requested-With': 'XMLHttpRequest',
                ...additionalHeaderPropertiesForCustomMode,
                ...extendHttpRequestHeaders('X-CSRF-TOKEN', window.csrf_token),
                ...extendHttpRequestHeaders('X-UID', window.UID),
                ...extendHttpRequestHeaders('X-EMAIL', window.EMAIL),
            },
            body: requestBody.body instanceof FormData || processAsFormData ? requestBody.body : JSON.stringify(requestBody.body),
        };
        if (responseExpected) {
            return fetch(url, fetchConfig)
                .then(response => {
                    return checkResponse(response, dispatch, setError, actions, shouldShowSpinner, emptyResonse);
                })
                .catch(error => {
                    return global.__ENV__.PRODUCTION && console.log('failed at endpoint ---', url, 'error ---', error);
                });
        } else {
            fetch(url, fetchConfig);
        }
    },
};
